import { lazy } from 'react';

import { createBrowserRouter, Navigate } from 'react-router';

import { AssessmentShell, StaticShell } from '@/components/layout/AssessmentShell';
import { AppSuspense } from '@/components/ui/Suspense';

const WelcomePage = lazy(() =>
  import('@/features/onboarding/pages/WelcomePage').then((m) => ({ default: m.WelcomePage })),
);
const HonestyPolicyPage = lazy(() =>
  import('@/features/onboarding/pages/HonestyPolicyPage').then((m) => ({
    default: m.HonestyPolicyPage,
  })),
);
const CameraSetupPage = lazy(() =>
  import('@/features/onboarding/pages/CameraSetupPage').then((m) => ({
    default: m.CameraSetupPage,
  })),
);
const AssessmentPage = lazy(() =>
  import('@/features/assessment/pages/AssessmentPage').then((m) => ({
    default: m.AssessmentPage,
  })),
);
const FeedbackPage = lazy(() =>
  import('@/features/feedback/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })),
);
const CompletionPage = lazy(() =>
  import('@/features/completion/pages/CompletionPage').then((m) => ({
    default: m.CompletionPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const ExpiredPage = lazy(() =>
  import('@/pages/ExpiredPage').then((m) => ({ default: m.ExpiredPage })),
);
const AlreadyCompletedPage = lazy(() =>
  import('@/pages/AlreadyCompletedPage').then((m) => ({ default: m.AlreadyCompletedPage })),
);
const UnavailablePage = lazy(() =>
  import('@/pages/UnavailablePage').then((m) => ({ default: m.UnavailablePage })),
);

export const router = createBrowserRouter([
  // Token-based assessment flow
  {
    path: '/:token',
    element: (
      <AppSuspense>
        <AssessmentShell />
      </AppSuspense>
    ),
    children: [
      {
        index: true,
        element: (
          <AppSuspense variant="skeleton">
            <WelcomePage />
          </AppSuspense>
        ),
      },
      {
        path: 'policy',
        element: (
          <AppSuspense variant="skeleton">
            <HonestyPolicyPage />
          </AppSuspense>
        ),
      },
      {
        path: 'camera',
        element: (
          <AppSuspense variant="skeleton">
            <CameraSetupPage />
          </AppSuspense>
        ),
      },
      {
        path: 'exam',
        element: (
          <AppSuspense variant="skeleton">
            <AssessmentPage />
          </AppSuspense>
        ),
      },
      {
        path: 'feedback',
        element: (
          <AppSuspense variant="skeleton">
            <FeedbackPage />
          </AppSuspense>
        ),
      },
      {
        path: 'complete',
        element: (
          <AppSuspense variant="skeleton">
            <CompletionPage />
          </AppSuspense>
        ),
      },
    ],
  },
  // Static pages — share the same navbar + column chrome via StaticShell
  {
    element: (
      <AppSuspense>
        <StaticShell />
      </AppSuspense>
    ),
    children: [
      {
        path: '/not-found',
        element: (
          <AppSuspense variant="skeleton">
            <NotFoundPage />
          </AppSuspense>
        ),
      },
      {
        path: '/expired',
        element: (
          <AppSuspense variant="skeleton">
            <ExpiredPage />
          </AppSuspense>
        ),
      },
      {
        path: '/already-completed',
        element: (
          <AppSuspense variant="skeleton">
            <AlreadyCompletedPage />
          </AppSuspense>
        ),
      },
    ],
  },
  // Unavailable page — under /:token so the token survives refresh for reporting,
  // but outside AssessmentShell to avoid the route guard redirect loop.
  {
    path: '/:token/unavailable',
    element: (
      <AppSuspense>
        <StaticShell />
      </AppSuspense>
    ),
    children: [
      {
        index: true,
        element: (
          <AppSuspense variant="skeleton">
            <UnavailablePage />
          </AppSuspense>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <Navigate to="/not-found" replace />,
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
]);
